// NOTE: This policy assumes a cookie consent mechanism will be added before AdSense
// activation (~July 7, 2026). When that work begins, use Google's Funding Choices tool —
// it handles both consent mode and AdSense integration in one setup.

import React from 'react';
import { Link } from 'react-router-dom';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-secondary-50">
      <header className="bg-white shadow-sm border-b border-secondary-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center">
              <span className="font-display text-2xl tracking-tight leading-none text-secondary-900">
                ONLY<span className="text-gold-700">KICKERS</span>
              </span>
            </Link>
            <Link to="/login" className="text-sm font-medium text-primary-600 hover:text-primary-500">
              Sign in
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-card px-8 py-10">

          <h1 className="font-display uppercase text-4xl leading-none text-ink-900 mb-2">Privacy Policy</h1>
          <p className="text-sm text-secondary-500 mb-8">Effective Date: [Effective Date]</p>

          <Section>
            <p>[OnlyKickers LLC] ("OnlyKickers," "we," "us," or "our") operates onlykickers.com (the "Service"). This Privacy Policy explains what information we collect, how we use it, and the choices you have. By using the Service, you agree to the practices described here.</p>
          </Section>

          <Section heading="1. Information We Collect">
            <p><strong>Account information.</strong> When you create an account, we collect your email address, a username, and a password (hashed, never stored in plain text).</p>
            <p><strong>Fantasy gameplay data.</strong> We store the leagues you create or join, your draft picks, lineups, trades, waiver claims, and other actions you take inside the Service.</p>
            <p><strong>Automatically collected data.</strong> When you use the Service, we automatically collect information such as your IP address, browser type, device type, operating system, pages viewed, and timestamps. We use cookies and similar technologies (described below) to collect some of this information.</p>
            <p><strong>Communications.</strong> If you contact us at support@onlykickers.com or respond to emails we send, we keep a record of that correspondence.</p>
            <p>We do not knowingly collect Social Security numbers, financial account information, government IDs, or precise geolocation data.</p>
          </Section>

          <Section heading="2. How We Use Information">
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide, operate, and maintain the Service</li>
              <li>Create and manage your account</li>
              <li>Run fantasy leagues, drafts, scoring, and related features</li>
              <li>Send transactional emails (password resets, league invitations, account notifications)</li>
              <li>Display advertising through Google AdSense</li>
              <li>Monitor and improve the Service, diagnose errors, and prevent abuse</li>
              <li>Comply with legal obligations and enforce our Terms of Service</li>
            </ul>
          </Section>

          <Section heading="3. Cookies and Similar Technologies">
            <p>We and our third-party partners use cookies, local storage, and similar technologies to keep you signed in, remember your preferences, measure how the Service is used, and serve advertising. You can control cookies through your browser settings. Disabling cookies may prevent parts of the Service from working.</p>
            <p>When required by applicable law, we will request your consent for non-essential cookies through an in-site banner before they are set.</p>
          </Section>

          <Section heading="4. Advertising and Google AdSense">
            <p>We use Google AdSense to display ads. Google and its partners use cookies to serve ads based on your prior visits to our Service and other websites. You can opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">Google's Ads Settings</a> or the <a href="https://www.networkadvertising.org/choices/" target="_blank" rel="noopener noreferrer">Network Advertising Initiative opt-out page</a>. For more information about how Google uses data, see <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">policies.google.com/technologies/partner-sites</a>.</p>
          </Section>

          <Section heading="5. Third-Party Service Providers">
            <p>We share information with third parties that help us operate the Service:</p>
            <ul>
              <li><strong>Google AdSense</strong> — advertising</li>
              <li><strong>Postmark</strong> — transactional email delivery</li>
              <li><strong>Sentry</strong> — error monitoring and crash reporting</li>
              <li><strong>Our hosting provider</strong> — server infrastructure and data storage</li>
              <li><strong>Analytics provider</strong> — aggregate usage measurement</li>
            </ul>
            <p>These providers process information on our behalf and are bound by their own privacy practices. We do not sell your personal information.</p>
          </Section>

          <Section heading="6. Legal Disclosures">
            <p>We may disclose information when we believe in good faith that disclosure is necessary to comply with a law, regulation, legal process, or governmental request; to enforce our Terms of Service; to protect the rights, property, or safety of OnlyKickers, our users, or others; or in connection with a merger, acquisition, or sale of assets.</p>
          </Section>

          <Section heading="7. Data Retention">
            <p>We retain account information for as long as your account is active. If you delete your account, we will delete or anonymize your personal information within a reasonable period, except where we are required to retain it to comply with legal obligations, resolve disputes, or enforce our agreements. Backups containing your information may persist for a limited additional period before being overwritten.</p>
          </Section>

          <Section heading="8. Security">
            <p>We use commercially reasonable measures to protect the information we collect, including encryption in transit (HTTPS), hashed password storage, error monitoring, and access controls. No method of transmission or storage is perfectly secure, and we cannot guarantee absolute security.</p>
          </Section>

          <Section heading="9. Children's Privacy">
            <p>The Service is not intended for children under 13. We do not knowingly collect personal information from anyone under 13. If you believe a child under 13 has provided us information, please contact us at support@onlykickers.com and we will delete it.</p>
          </Section>

          <Section heading="10. Your Rights">
            <p>Depending on where you live, you may have rights to access, correct, delete, or export the personal information we hold about you, and to object to or restrict certain processing. To exercise these rights, contact us at support@onlykickers.com. We will respond within the timeframe required by applicable law.</p>
            <p><strong>California residents.</strong> Under the California Consumer Privacy Act (CCPA/CPRA), you have the right to know what personal information we collect, the right to delete it, the right to correct it, and the right to opt out of "sale" or "sharing" of personal information. We do not sell personal information for money. To the extent advertising cookies constitute "sharing" under the CCPA, you may opt out through your browser's Global Privacy Control signal or by contacting us.</p>
            <p><strong>EEA/UK residents.</strong> If you are in the European Economic Area or United Kingdom, our legal bases for processing are: performance of a contract (operating your account), legitimate interests (improving and securing the Service), consent (non-essential cookies and advertising where required), and compliance with legal obligations. You have the right to lodge a complaint with your local supervisory authority.</p>
          </Section>

          <Section heading="11. International Transfers">
            <p>We operate from the United States. If you access the Service from outside the U.S., your information will be transferred to and processed in the U.S., which may have different data protection laws than your country.</p>
          </Section>

          <Section heading="12. Changes to This Policy">
            <p>We may update this Privacy Policy from time to time. We will post the updated version on this page and update the "Effective Date" above. If changes are material, we will provide additional notice (such as an email or in-app notification).</p>
          </Section>

          <Section heading="13. Contact Us">
            <address className="not-italic">
              <p>[OnlyKickers LLC]</p>
              <p>[Toledo, OH street address]</p>
              <p><a href="mailto:support@onlykickers.com">support@onlykickers.com</a></p>
            </address>
          </Section>

        </div>
      </main>

      <footer className="bg-white border-t border-secondary-200 mt-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 text-sm text-secondary-500">
            <span>&copy; 2026 OnlyKickers</span>
            <span className="hidden sm:inline">·</span>
            <Link to="/privacy" className="hover:text-secondary-700">Privacy Policy</Link>
            <span className="hidden sm:inline">·</span>
            <Link to="/terms" className="hover:text-secondary-700">Terms of Service</Link>
            <span className="hidden sm:inline">·</span>
            <a href="mailto:support@onlykickers.com" className="hover:text-secondary-700">support@onlykickers.com</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Section({ heading, children }: { heading?: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      {heading && (
        <h2 className="text-lg font-semibold text-secondary-900 mb-3 pb-1 border-b border-secondary-100">
          {heading}
        </h2>
      )}
      <div className="space-y-3 text-secondary-700 text-sm leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_a]:text-primary-600 [&_a:hover]:text-primary-500 [&_a]:underline">
        {children}
      </div>
    </section>
  );
}
